// Admin outreach queue.
//
// Channel tabs (WA / TG) + filters by role / tier / status + a list of
// message cards. Each card shows the LLM draft with edit-in-place,
// Approve / Reject / Regenerate buttons.
//
// First-time entry: empty state with a "Generate first batch" button
// that calls POST /api/admin/outreach/messages/generate with default
// filters (channel-tab + role/tier filters honoured).

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Send, Check, X, RefreshCw, MessageSquare, Sparkles, Repeat, Zap } from 'lucide-react';
import { api, ApiError } from '../api';
import { Shell } from '../components/layout/Shell';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { EmptyState } from '../components/ui/empty-state';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useToast } from '../lib/toast';
import { cn, fmtRelative } from '../lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

type Channel = 'wa' | 'tg';
type Role = 'merchant' | 'dp' | 'unknown';
type Tier = 'high' | 'medium' | 'low' | 'very_low';
type Status =
  | 'open'
  | 'drafting'
  | 'drafted'
  | 'approved'
  | 'edited'
  | 'rejected'
  | 'pending'
  | 'sent'
  | 'failed'
  | 'replied';

interface OutreachMessageRow {
  id: string;
  target_id: string;
  status: Status;
  draft_text: string | null;
  final_text: string | null;
  llm_model: string | null;
  llm_provider: string | null;
  approved_by: string | null;
  approved_at: string | null;
  scheduled_for: string | null;
  sent_at: string | null;
  reply_text: string | null;
  replied_at: string | null;
  failed_reason: string | null;
  created_at: string;
  updated_at: string;
  // Joined target columns
  channel: Channel;
  identifier: string;
  role: Role;
  tier: Tier;
  push_name: string | null;
  username: string | null;
  inferred_business: string | null;
  area_hint: string | null;
  evidence: string | null;
  subtype: string | null;
  in_groups: string[] | null;
}

const STATUS_BADGE: Record<Status, 'default' | 'open' | 'accepted' | 'completed' | 'cancelled' | 'urgent' | 'brand'> = {
  open: 'default',
  drafting: 'default',
  drafted: 'open',
  approved: 'accepted',
  edited: 'accepted',
  rejected: 'cancelled',
  pending: 'open',
  sent: 'completed',
  failed: 'urgent',
  replied: 'brand',
};

export function OutreachPage() {
  const [params, setParams] = useSearchParams();
  const channel = (params.get('channel') ?? 'tg') as Channel;
  const role = params.get('role') ?? '';
  const tier = params.get('tier') ?? '';
  const status = params.get('status') ?? 'drafted';

  const [rows, setRows] = useState<OutreachMessageRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [statsKey, setStatsKey] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [bulkApproving, setBulkApproving] = useState(false);
  const [editText, setEditText] = useState<Record<string, string>>({});
  const { show: toast } = useToast();

  function patch(next: Record<string, string>) {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v == null || v === '') merged.delete(k);
      else merged.set(k, v);
    }
    setParams(merged);
  }

  function load() {
    setRows(null);
    setErr(null);
    setStatsKey((k) => k + 1);
    const qs = new URLSearchParams();
    qs.set('channel', channel);
    if (role) qs.set('role', role);
    if (tier) qs.set('tier', tier);
    if (status) qs.set('status', status);
    qs.set('limit', '50');
    api<{ messages: OutreachMessageRow[] }>(`/api/admin/outreach/messages?${qs}`)
      .then((r) => setRows(r.messages))
      .catch((e) => setErr(e instanceof ApiError ? `Error ${e.status}` : 'Could not load'));
  }
  useEffect(load, [channel, role, tier, status]);

  async function generateBatch() {
    setGenerating(true);
    try {
      const r = await api<{ queued: number }>(
        '/api/admin/outreach/messages/generate',
        {
          method: 'POST',
          json: {
            channel,
            ...(role ? { role } : {}),
            ...(tier ? { tier } : {}),
            limit: 20,
          },
        },
      );
      toast(`Queued ${r.queued} drafts`, 'success');
      // Switch to "drafting" view so the user sees the drafts flow in
      patch({ status: 'drafting' });
      setTimeout(load, 1500);
    } catch (e) {
      toast(e instanceof ApiError ? `Error ${e.status}` : 'Failed', 'error');
    } finally {
      setGenerating(false);
    }
  }

  // Approve every drafted message currently in view — one PATCH per row
  // against the same endpoint as the per-card Approve. allSettled so one
  // failure doesn't abort the batch.
  async function bulkApprove() {
    const targets = (rows ?? []).filter((m) => m.status === 'drafted');
    if (targets.length === 0) return;
    if (
      !window.confirm(
        `Approve all ${targets.length} drafted message${targets.length === 1 ? '' : 's'}? ` +
          `They enter the send queue and go out paced through the daily 9am–6pm window.`,
      )
    ) {
      return;
    }
    setBulkApproving(true);
    const results = await Promise.allSettled(
      targets.map((m) =>
        api(`/api/admin/outreach/messages/${m.id}`, {
          method: 'PATCH',
          json: { action: 'approve' },
        }),
      ),
    );
    const ok = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.length - ok;
    setBulkApproving(false);
    toast(
      failed === 0 ? `Approved ${ok}` : `Approved ${ok}, ${failed} failed`,
      failed === 0 ? 'success' : 'error',
    );
    load();
  }

  async function action(id: string, act: 'approve' | 'reject' | 'edit', text?: string) {
    setBusyId(id);
    try {
      await api(`/api/admin/outreach/messages/${id}`, {
        method: 'PATCH',
        json: { action: act, ...(text ? { text } : {}) },
      });
      toast(
        act === 'approve' ? 'Approved' : act === 'reject' ? 'Rejected' : 'Saved edit (approved)',
        'success',
      );
      setEditText((p) => { const n = { ...p }; delete n[id]; return n; });
      load();
    } catch (e) {
      const msg = e instanceof ApiError ? (e.body as { error?: string })?.error ?? `Error ${e.status}` : 'Failed';
      toast(msg, 'error');
    } finally {
      setBusyId(null);
    }
  }

  async function regenerate(id: string) {
    setBusyId(id);
    try {
      await api(`/api/admin/outreach/messages/${id}/regenerate`, { method: 'POST' });
      toast('Regenerating…', 'info');
      setTimeout(load, 2000);
    } catch (e) {
      toast(e instanceof ApiError ? `Error ${e.status}` : 'Failed', 'error');
    } finally {
      setBusyId(null);
    }
  }

  const empty = rows && rows.length === 0;
  const draftedCount = rows?.filter((m) => m.status === 'drafted').length ?? 0;

  return (
    <Shell title="Outreach">
      <PageHeader
        title="Outreach"
        description="LLM-drafted cold DMs awaiting your approval, then paced through the daily send window."
        actions={
          <div className="flex items-center gap-2">
            {draftedCount > 0 && (
              <Button
                variant="secondary"
                onClick={bulkApprove}
                disabled={bulkApproving || generating}
              >
                <Check className="h-4 w-4" />
                {bulkApproving ? 'Approving…' : `Approve all drafted (${draftedCount})`}
              </Button>
            )}
            <Button onClick={generateBatch} disabled={generating}>
              <Sparkles className="h-4 w-4" />
              {generating ? 'Queueing…' : 'Generate drafts (next 20)'}
            </Button>
          </div>
        }
      />

      <OutreachStats
        channel={channel}
        activeStatus={status}
        refreshKey={statsKey}
        onPick={(s) => patch({ status: s })}
      />

      <AutoDraftPanel />
      <AutoApprovePanel />
      <SenderCapsPanel />

      <Tabs value={channel} onValueChange={(v) => patch({ channel: v })} className="mb-4">
        <TabsList>
          <TabsTrigger value="tg">Telegram</TabsTrigger>
          <TabsTrigger value="wa">WhatsApp</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card className="mb-4">
        <CardContent className="flex flex-col gap-2 p-4 md:flex-row md:items-center md:gap-2">
          <Select value={status || 'all'} onValueChange={(v) => patch({ status: v === 'all' ? '' : v })}>
            <SelectTrigger className="md:w-36">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="drafted">Drafted (review)</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="edited">Edited</SelectItem>
              <SelectItem value="pending">Pending send</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="replied">Replied</SelectItem>
              <SelectItem value="open">Open (no draft yet)</SelectItem>
              <SelectItem value="drafting">Drafting…</SelectItem>
            </SelectContent>
          </Select>
          <Select value={role || 'any'} onValueChange={(v) => patch({ role: v === 'any' ? '' : v })}>
            <SelectTrigger className="md:w-36">
              <SelectValue placeholder="Role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any role</SelectItem>
              <SelectItem value="merchant">Merchant</SelectItem>
              <SelectItem value="dp">Delivery partner</SelectItem>
              <SelectItem value="unknown">Unknown</SelectItem>
            </SelectContent>
          </Select>
          <Select value={tier || 'any'} onValueChange={(v) => patch({ tier: v === 'any' ? '' : v })}>
            <SelectTrigger className="md:w-36">
              <SelectValue placeholder="Tier" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any tier</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="very_low">Very low</SelectItem>
            </SelectContent>
          </Select>
          {(role || tier || status !== 'drafted') && (
            <Button variant="ghost" size="sm" onClick={() => setParams(new URLSearchParams({ channel }))}>
              <X className="h-3.5 w-3.5" /> Reset
            </Button>
          )}
        </CardContent>
      </Card>

      {err && (
        <Card className="mb-4 border-error/40 bg-error-tint">
          <CardContent className="py-3 text-sm text-error-on-tint">{err}</CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {!rows &&
          Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <Skeleton className="h-24 w-full" />
              </CardContent>
            </Card>
          ))}

        {empty && (
          <Card>
            <CardContent>
              <EmptyState
                icon={MessageSquare}
                title="Nothing to show here."
                description={
                  status === 'drafted'
                    ? "Click 'Generate drafts (next 20)' to queue the LLM."
                    : 'Try changing the filters above.'
                }
              />
            </CardContent>
          </Card>
        )}

        {rows?.map((m) => {
          const recipientLine = [
            m.push_name,
            m.username ? `@${m.username}` : null,
            m.inferred_business,
          ].filter(Boolean).join(' · ');
          const text = editText[m.id] ?? m.final_text ?? m.draft_text ?? '';
          const editing = m.id in editText;
          const canEdit = ['drafted', 'edited', 'failed', 'approved', 'rejected'].includes(m.status);
          return (
            <Card key={m.id}>
              <CardContent className="space-y-3 p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={STATUS_BADGE[m.status] ?? 'default'}>{m.status}</Badge>
                  <Badge variant="default">{m.channel.toUpperCase()}</Badge>
                  <Badge variant="default">{m.role}</Badge>
                  <Badge variant="default">tier: {m.tier}</Badge>
                  <span className="text-xs text-text-muted">
                    {recipientLine || m.identifier}
                  </span>
                  <span className="ml-auto text-xs text-text-muted">
                    {m.llm_model ? `${m.llm_provider}/${m.llm_model} · ` : ''}
                    {fmtRelative(m.updated_at)}
                  </span>
                </div>

                {m.evidence && (
                  <p className="rounded-md bg-surface-muted px-3 py-2 text-xs italic text-text-subtle">
                    {m.evidence.slice(0, 240)}
                  </p>
                )}

                {m.status === 'failed' && m.failed_reason && (
                  <p className="rounded-md border border-error/40 bg-error-tint px-3 py-2 text-xs text-error-on-tint">
                    {m.failed_reason}
                  </p>
                )}

                {text ? (
                  editing ? (
                    <textarea
                      className="min-h-[120px] w-full rounded-md border border-border bg-surface p-3 text-sm text-text focus:border-border-emphasis focus:outline-none"
                      value={text}
                      onChange={(e) => setEditText((p) => ({ ...p, [m.id]: e.target.value }))}
                    />
                  ) : (
                    <p className="whitespace-pre-wrap rounded-md border border-border bg-surface p-3 text-sm text-text">
                      {text}
                    </p>
                  )
                ) : (
                  <p className="rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-text-muted">
                    No draft yet
                  </p>
                )}

                {m.status === 'replied' && (
                  <div className="rounded-md border border-border bg-brand-50 px-3 py-2 dark:bg-brand-900">
                    <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-brand-700 dark:text-brand-500">
                      <MessageSquare className="h-3 w-3" />
                      Reply{m.replied_at ? ` · ${fmtRelative(m.replied_at)}` : ''}
                    </div>
                    {m.reply_text && m.reply_text.trim() ? (
                      <p className="whitespace-pre-wrap text-sm text-text">{m.reply_text}</p>
                    ) : (
                      <p className="text-xs italic text-text-muted">
                        Non-text message (sticker, photo, or voice note) — open the chat to read it.
                      </p>
                    )}
                  </div>
                )}

                {canEdit && (
                  <div className="flex flex-wrap items-center gap-2">
                    {!editing && (
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={busyId === m.id || !text}
                        onClick={() => setEditText((p) => ({ ...p, [m.id]: text }))}
                      >
                        Edit
                      </Button>
                    )}
                    {editing && (
                      <>
                        <Button
                          variant="primary"
                          size="sm"
                          disabled={busyId === m.id}
                          onClick={() => action(m.id, 'edit', editText[m.id])}
                        >
                          <Check className="h-3.5 w-3.5" /> Save edit (approves)
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditText((p) => { const n = { ...p }; delete n[m.id]; return n; })}
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                    {!editing && m.status !== 'approved' && m.status !== 'edited' && (
                      <Button
                        variant="primary"
                        size="sm"
                        disabled={busyId === m.id || !text}
                        onClick={() => action(m.id, 'approve')}
                      >
                        <Check className="h-3.5 w-3.5" /> Approve
                      </Button>
                    )}
                    {!editing && m.status !== 'rejected' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busyId === m.id}
                        onClick={() => action(m.id, 'reject')}
                      >
                        <X className="h-3.5 w-3.5" /> Reject
                      </Button>
                    )}
                    {!editing && (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busyId === m.id}
                        onClick={() => regenerate(m.id)}
                      >
                        <RefreshCw className="h-3.5 w-3.5" /> Regenerate
                      </Button>
                    )}
                  </div>
                )}

                {m.status === 'sent' && m.sent_at && (
                  <p className="text-xs text-status-completed-on-tint">
                    <Send className="mr-1 inline h-3 w-3" />
                    Sent {fmtRelative(m.sent_at)}
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </Shell>
  );
}

// ---------- Stats strip --------------------------------------------------
//
// Per-channel counters above the queue: drafts awaiting review, messages
// queued to send, how many have gone out (with today's pace), failures,
// and replies. Each tile is a shortcut that applies the matching status
// filter to the list below.

interface OutreachStatsResponse {
  total_targets: number;
  by_status: Array<{ channel: Channel; status: Status; c: number }>;
  sent_today: Array<{ channel: Channel; c: number }>;
}

const TONE_TEXT: Record<string, string> = {
  neutral: 'text-text',
  accepted: 'text-status-accepted-on-tint',
  completed: 'text-status-completed-on-tint',
  error: 'text-error-on-tint',
  brand: 'text-brand-700 dark:text-brand-500',
};

function OutreachStats({
  channel,
  activeStatus,
  refreshKey,
  onPick,
}: {
  channel: Channel;
  activeStatus: string;
  refreshKey: number;
  onPick: (status: string) => void;
}) {
  const [data, setData] = useState<OutreachStatsResponse | null>(null);

  useEffect(() => {
    api<OutreachStatsResponse>('/api/admin/outreach/stats')
      .then(setData)
      .catch(() => undefined);
  }, [refreshKey]);

  if (!data) return null;

  const counts: Partial<Record<Status, number>> = {};
  for (const r of data.by_status) {
    if (r.channel === channel) counts[r.status] = (counts[r.status] ?? 0) + r.c;
  }
  const sentToday = data.sent_today.find((r) => r.channel === channel)?.c ?? 0;
  const queued =
    (counts.approved ?? 0) + (counts.edited ?? 0) + (counts.pending ?? 0);

  const tiles: Array<{
    key: string;
    label: string;
    value: number;
    hint?: string;
    filter: string;
    tone: keyof typeof TONE_TEXT;
  }> = [
    { key: 'drafted', label: 'Awaiting review', value: counts.drafted ?? 0, filter: 'drafted', tone: 'neutral' },
    { key: 'queued', label: 'Queued to send', value: queued, filter: 'approved', tone: 'accepted' },
    { key: 'sent', label: 'Sent', value: counts.sent ?? 0, hint: `${sentToday} today`, filter: 'sent', tone: 'completed' },
    { key: 'failed', label: 'Failed', value: counts.failed ?? 0, filter: 'failed', tone: 'error' },
    { key: 'replied', label: 'Replied', value: counts.replied ?? 0, filter: 'replied', tone: 'brand' },
  ];

  return (
    <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
      {tiles.map((t) => {
        const active = activeStatus === t.filter;
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => onPick(t.filter)}
            className={cn(
              'rounded-lg border bg-surface px-3 py-2.5 text-left transition-colors hover:border-border-emphasis',
              active
                ? 'border-border-emphasis ring-1 ring-border-emphasis'
                : 'border-border',
            )}
          >
            <div className={cn('tnum text-2xl font-bold leading-tight', TONE_TEXT[t.tone])}>
              {t.value}
            </div>
            <div className="mt-0.5 text-xs font-medium text-text-muted">
              {t.label}
              {t.hint && <span className="ml-1 text-text-subtle">· {t.hint}</span>}
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ---------- Auto-draft panel ---------------------------------------------
//
// Compact strip above the queue showing the auto-refill state. Lets the
// admin toggle the loop on/off and edit the target queue depth. Filter
// columns (channel/role/tier) inherit from the page-level filters when
// "Apply current filters" is on, otherwise stays unfiltered.

interface AutoDraftConfigRow {
  enabled: boolean;
  target_drafted_count: number;
  channel: Channel | null;
  role: Role | null;
  tier: Tier | null;
  last_run_at: string | null;
  last_generated_count: number | null;
  last_error: string | null;
  updated_at: string;
}

function AutoDraftPanel() {
  const [cfg, setCfg] = useState<AutoDraftConfigRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [targetInput, setTargetInput] = useState<string>('');
  const { show: toast } = useToast();

  function load() {
    api<AutoDraftConfigRow>('/api/admin/outreach/auto-draft')
      .then((r) => {
        setCfg(r);
        setTargetInput(String(r.target_drafted_count));
      })
      .catch(() => undefined);
  }
  useEffect(load, []);

  async function patch(body: Partial<AutoDraftConfigRow>) {
    setBusy(true);
    try {
      const r = await api<AutoDraftConfigRow>('/api/admin/outreach/auto-draft', {
        method: 'PATCH',
        json: body,
      });
      setCfg(r);
      setTargetInput(String(r.target_drafted_count));
    } catch (e) {
      toast(e instanceof ApiError ? `Error ${e.status}` : 'Update failed', 'error');
    } finally {
      setBusy(false);
    }
  }

  if (!cfg) return null;

  const lastRunNote = cfg.last_run_at
    ? `last tick ${fmtRelative(cfg.last_run_at)}${cfg.last_generated_count != null ? ` · queued ${cfg.last_generated_count}` : ''}`
    : 'no tick yet';

  return (
    <Card className="mb-4">
      <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-center">
        <div className="flex items-center gap-2">
          <Repeat className="h-4 w-4 text-text-muted" />
          <span className="font-semibold text-text">Auto-refill drafts</span>
          <Badge variant={cfg.enabled ? 'completed' : 'cancelled'}>
            {cfg.enabled ? 'ON' : 'OFF'}
          </Badge>
        </div>

        <div className="flex flex-wrap items-center gap-2 md:ml-2">
          <span className="text-xs text-text-muted">Keep</span>
          <Input
            type="number"
            min={0}
            max={200}
            className="h-8 w-20"
            value={targetInput}
            onChange={(e) => setTargetInput(e.target.value)}
            onBlur={() => {
              const n = Math.max(0, Math.min(200, Math.floor(Number(targetInput) || 0)));
              if (n !== cfg.target_drafted_count) void patch({ target_drafted_count: n });
              else setTargetInput(String(cfg.target_drafted_count));
            }}
          />
          <span className="text-xs text-text-muted">
            drafted {cfg.channel ? '' : 'per channel '}at all times. Tick every 5 min.
          </span>
        </div>

        <div className="md:ml-auto flex shrink-0 items-center gap-2">
          <span className="hidden text-xs text-text-muted md:inline">{lastRunNote}</span>
          <Button
            variant={cfg.enabled ? 'ghost' : 'primary'}
            size="sm"
            disabled={busy}
            onClick={() => patch({ enabled: !cfg.enabled })}
          >
            {cfg.enabled ? 'Pause auto-refill' : 'Enable auto-refill'}
          </Button>
        </div>

        {cfg.last_error && (
          <p className="basis-full rounded-md border border-error/40 bg-error-tint px-3 py-2 text-xs text-error-on-tint">
            last error: {cfg.last_error}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ---------- Auto-approve panel -------------------------------------------
//
// Per-channel toggles (WA / TG) that flip drafted/edited messages to
// approved without a human click. Throughput is bounded downstream by
// outreach_sender_accounts.daily_cap on the send worker, so this stage
// is intentionally unthrottled. Set the per-day send rate on the
// /sender-accounts page.

interface AutoApproveConfigRow {
  wa_enabled: boolean;
  tg_enabled: boolean;
  last_run_at: string | null;
  last_approved_count: number | null;
  last_error: string | null;
  updated_at: string;
}

function AutoApprovePanel() {
  const [cfg, setCfg] = useState<AutoApproveConfigRow | null>(null);
  const [busy, setBusy] = useState(false);
  const { show: toast } = useToast();

  function load() {
    api<AutoApproveConfigRow>('/api/admin/outreach/auto-approve')
      .then(setCfg)
      .catch(() => undefined);
  }
  useEffect(load, []);

  async function patch(body: Partial<AutoApproveConfigRow>) {
    setBusy(true);
    try {
      const r = await api<AutoApproveConfigRow>('/api/admin/outreach/auto-approve', {
        method: 'PATCH',
        json: body,
      });
      setCfg(r);
    } catch (e) {
      toast(e instanceof ApiError ? `Error ${e.status}` : 'Update failed', 'error');
    } finally {
      setBusy(false);
    }
  }

  if (!cfg) return null;

  const lastRunNote = cfg.last_run_at
    ? `last tick ${fmtRelative(cfg.last_run_at)}${cfg.last_approved_count != null ? ` · approved ${cfg.last_approved_count}` : ''}`
    : 'no tick yet';

  return (
    <Card className="mb-4">
      <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-center">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-text-muted" />
          <span className="font-semibold text-text">Auto-approve drafts</span>
        </div>

        <div className="flex flex-wrap items-center gap-2 md:ml-2">
          <Button
            variant={cfg.wa_enabled ? 'primary' : 'ghost'}
            size="sm"
            disabled={busy}
            onClick={() => patch({ wa_enabled: !cfg.wa_enabled })}
          >
            WA <Badge variant={cfg.wa_enabled ? 'completed' : 'cancelled'} className="ml-2">
              {cfg.wa_enabled ? 'AUTO' : 'MANUAL'}
            </Badge>
          </Button>
          <Button
            variant={cfg.tg_enabled ? 'primary' : 'ghost'}
            size="sm"
            disabled={busy}
            onClick={() => patch({ tg_enabled: !cfg.tg_enabled })}
          >
            TG <Badge variant={cfg.tg_enabled ? 'completed' : 'cancelled'} className="ml-2">
              {cfg.tg_enabled ? 'AUTO' : 'MANUAL'}
            </Badge>
          </Button>
          <span className="text-xs text-text-muted">
            Per-day send rate set on Senders → daily cap. Tick every 1 min.
          </span>
        </div>

        <div className="md:ml-auto flex shrink-0 items-center gap-2">
          <span className="hidden text-xs text-text-muted md:inline">{lastRunNote}</span>
        </div>

        {cfg.last_error && (
          <p className="basis-full rounded-md border border-error/40 bg-error-tint px-3 py-2 text-xs text-error-on-tint">
            last error: {cfg.last_error}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ---------- Sender daily-cap inline editor -------------------------------
//
// Inline number inputs for each enabled sender account's daily_cap, so
// the founder can adjust send pace from the outreach queue itself without
// jumping to /sender-accounts. Disabled senders are hidden — they don't
// participate in pacing.

interface SenderAccountRow {
  id: string;
  channel: 'wa' | 'tg';
  identifier: string;
  display_name: string | null;
  daily_cap: number;
  enabled: boolean;
}

function SenderCapsPanel() {
  const [rows, setRows] = useState<SenderAccountRow[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const { show: toast } = useToast();

  function load() {
    api<{ accounts: SenderAccountRow[] }>('/api/admin/outreach/sender-accounts')
      .then((r) => setRows(r.accounts.filter((a) => a.enabled)))
      .catch(() => undefined);
  }
  useEffect(load, []);

  async function setCap(id: string, daily_cap: number) {
    setBusyId(id);
    try {
      await api(`/api/admin/outreach/sender-accounts/${id}`, {
        method: 'PATCH',
        json: { daily_cap },
      });
      setRows((prev) =>
        prev ? prev.map((r) => (r.id === id ? { ...r, daily_cap } : r)) : null,
      );
    } catch (e) {
      toast(e instanceof ApiError ? `Error ${e.status}` : 'Update failed', 'error');
    } finally {
      setBusyId(null);
    }
  }

  if (!rows || rows.length === 0) return null;

  return (
    <Card className="mb-4">
      <CardContent className="flex flex-wrap items-center gap-3 p-4">
        <span className="font-semibold text-text">Daily send caps</span>
        {rows.map((r) => (
          <DailyCapInline
            key={r.id}
            channel={r.channel}
            label={r.display_name ?? r.identifier}
            current={r.daily_cap}
            disabled={busyId === r.id}
            onSave={(n) => void setCap(r.id, n)}
          />
        ))}
        <span className="ml-auto text-xs text-text-muted">
          enforced by send worker · disabled senders hidden
        </span>
      </CardContent>
    </Card>
  );
}

interface DailyCapInlineProps {
  channel: 'wa' | 'tg';
  label: string;
  current: number;
  disabled: boolean;
  onSave: (n: number) => void;
}

function DailyCapInline({ channel, label, current, disabled, onSave }: DailyCapInlineProps) {
  const [val, setVal] = useState(String(current));
  useEffect(() => setVal(String(current)), [current]);
  return (
    <div className="flex items-center gap-1.5">
      <Badge variant={channel === 'tg' ? 'brand' : 'accepted'}>{channel.toUpperCase()}</Badge>
      <span className="text-xs text-text-muted">{label}</span>
      <Input
        type="number"
        min={0}
        max={500}
        className="h-8 w-20"
        value={val}
        disabled={disabled}
        onChange={(e) => setVal(e.target.value)}
        onBlur={() => {
          const n = Math.max(0, Math.min(500, Math.floor(Number(val) || 0)));
          if (n !== current) onSave(n);
          else setVal(String(current));
        }}
      />
      <span className="text-xs text-text-muted">/day</span>
    </div>
  );
}
